/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

import React from 'react';

/**
 * What the organizer sees when a save is refused.
 *
 * Three things this does that a generic "Something went wrong" cannot:
 *
 *  1. **States that nothing was written.** `execute_batch` runs in one
 *     transaction, so that is literally true, and it is the first thing anybody
 *     wants to know — did half my event just land?
 *
 *  2. **Prints the server's own reason verbatim.** The batch endpoint answers
 *     with things like "You are not allowed to create records in
 *     'event_activity'", which tells the organizer exactly what to ask an admin
 *     for. Replacing it with friendlier wording throws away the only actionable
 *     part of the response.
 *
 *  3. **Names the failed operation.** Which table, which position, out of how
 *     many — enough to tell a permissions problem from a data problem without
 *     opening a network tab.
 *
 * Retry is offered for everything except an expired session, where it cannot
 * help: the credentials are gone and pressing it again just fails identically.
 */

export default function SaveErrorPanel({ failure, operationCount, onRetry, retrying }) {
  if (!failure) return null;

  const canRetry = failure.reason !== 'session_expired';
  const hasLocation = !!failure.table;

  return (
    <div className="orgf-panel" role="alert">
      <h3>Nothing was saved.</h3>
      <p style={{ margin: 0 }}>{failure.message}</p>

      {hasLocation && (
        <p className="orgf-reason">
          Failed while writing <strong>{failure.table}</strong>
          {failure.index !== undefined && failure.index !== null && (
            <> (operation {failure.index + 1}{operationCount ? ` of ${operationCount}` : ''})</>
          )}
          .{failure.reason ? ` ${failure.reason}` : ''}
          {' '}The whole batch was rolled back, so nothing you entered has been lost — correct it and
          save again.
        </p>
      )}

      {canRetry && (
        <p style={{ margin: '11px 0 0' }}>
          <button type="button" className="org-btn ghost" onClick={onRetry} disabled={retrying}>
            {retrying ? 'Saving…' : 'Try saving again'}
          </button>
        </p>
      )}
    </div>
  );
}
